import React from 'react';
import { Group, Rect, Text } from 'react-konva';
import { useEditorStore, AisleZone } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';

// ─── Visual config per aisle type ─────────────────────────────────────────────
const AISLE_STYLE: Record<string, { fill: string; stroke: string; label: string }> = {
  cold:        { fill: 'rgba(56,189,248,0.08)',  stroke: 'rgba(56,189,248,0.35)',  label: 'ХОЛОДНЫЙ КОРИДОР' },
  hot:         { fill: 'rgba(249,115,22,0.08)',  stroke: 'rgba(249,115,22,0.35)',  label: 'ГОРЯЧИЙ КОРИДОР'  },
  containment: { fill: 'rgba(168,85,247,0.07)',  stroke: 'rgba(168,85,247,0.35)',  label: 'CONTAINMENT'      },
  indeterminate: { fill: 'rgba(239,68,68,0.10)', stroke: 'rgba(239,68,68,0.75)', label: 'ОШИБКА ОРИЕНТАЦИИ' },
};

const TEXT_COLOR: Record<string, string> = {
  cold:        'rgba(56,189,248,0.70)',
  hot:         'rgba(249,115,22,0.70)',
  containment: 'rgba(168,85,247,0.70)',
  indeterminate: 'rgba(248,113,113,0.95)',
};

// ─── Single zone ──────────────────────────────────────────────────────────────
interface AisleZoneShapeProps {
  zone: AisleZone;
  isSelected: boolean;
  stageScale: number;
  isDraggable: boolean;
  onSelect: (additive: boolean) => void;
  onDragEnd: (x: number, y: number) => void;
}

const AisleZoneShape: React.FC<AisleZoneShapeProps> = ({ zone, isSelected, stageScale, isDraggable, onSelect, onDragEnd }) => {
  const visualType = zone.derivedStatus === 'indeterminate'
    ? 'indeterminate'
    : zone.typeOverride?.aisleType ?? zone.aisleType;
  const style = AISLE_STYLE[visualType] ?? AISLE_STYLE.cold;
  const textColor = TEXT_COLOR[visualType] ?? TEXT_COLOR.cold;
  const sw = isSelected ? 2 / stageScale : 1 / stageScale;
  const displayLabel = zone.typeOverride
    ? `[ИСКЛЮЧЕНИЕ] ${zone.label || style.label}`
    : zone.label || style.label;
  const fontSize = Math.max(10, Math.min(14, zone.h * 0.22)) / stageScale;

  const isContained = Boolean(zone.isContained || zone.aisleType === 'containment');
  const isHorizontal = zone.w >= zone.h;
  const doorThickness = Math.max(3, 5 / stageScale);

  return (
    <Group
      x={zone.x}
      y={zone.y}
      draggable={isDraggable}
      onClick={e => { e.cancelBubble = true; onSelect(e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey); }}
      onDragEnd={e => {
        const node = e.target;
        onDragEnd(Math.round(node.x()), Math.round(node.y()));
      }}
    >
      <Rect
        x={0}
        y={0}
        width={zone.w}
        height={zone.h}
        fill={isContained ? 'rgba(168,85,247,0.12)' : style.fill}
        stroke={isSelected ? '#ffffff' : zone.typeOverride ? '#fbbf24' : (isContained ? 'rgba(168,85,247,0.7)' : style.stroke)}
        strokeWidth={isContained ? Math.max(sw, 1.5 / stageScale) : sw}
        dash={isContained ? undefined : [6 / stageScale, 3 / stageScale]}
        cornerRadius={4 / stageScale}
        listening
      />
      {/* Containment end doors */}
      {isContained && isHorizontal && (
        <>
          <Rect
            x={0}
            y={0}
            width={doorThickness}
            height={zone.h}
            fill="#c084fc"
            listening={false}
          />
          <Rect
            x={zone.w - doorThickness}
            y={0}
            width={doorThickness}
            height={zone.h}
            fill="#c084fc"
            listening={false}
          />
        </>
      )}
      {isContained && !isHorizontal && (
        <>
          <Rect
            x={0}
            y={0}
            width={zone.w}
            height={doorThickness}
            fill="#c084fc"
            listening={false}
          />
          <Rect
            x={0}
            y={zone.h - doorThickness}
            width={zone.w}
            height={doorThickness}
            fill="#c084fc"
            listening={false}
          />
        </>
      )}
      {/* Label — centered */}
      <Text
        x={0}
        y={zone.h / 2 - fontSize}
        width={zone.w}
        align="center"
        text={isContained && !displayLabel.includes('[ISO]') ? `[ISO] ${displayLabel}` : displayLabel}
        fontSize={fontSize}
        fontFamily="JetBrains Mono, monospace"
        fill={isContained ? '#c084fc' : textColor}
        fontStyle="bold"
        letterSpacing={2}
        listening={false}
      />
      {zone.derivedStatus === 'indeterminate' && zone.derivedReason && (
        <Text
          x={4 / stageScale}
          y={zone.h / 2 + fontSize}
          width={Math.max(0, zone.w - 8 / stageScale)}
          align="center"
          text={zone.derivedReason}
          fontSize={Math.max(9, fontSize * 0.72)}
          fill={textColor}
          listening={false}
        />
      )}
    </Group>
  );
};

// ─── Layer ────────────────────────────────────────────────────────────────────
export const AislesLayer: React.FC = () => {
  const { aisleZones, showAisles, selectedIds, toggleSelected, stageScale, tool, removeAisleZone, updateAisleZone } =
    useEditorStore(useShallow(state => ({
      aisleZones:    state.aisleZones,
      showAisles:    state.showAisles,
      selectedIds:   state.selectedIds,
      toggleSelected: state.toggleSelected,
      stageScale:    state.stageScale,
      tool:          state.tool,
      removeAisleZone: state.removeAisleZone,
      updateAisleZone: state.updateAisleZone,
    })));

  if (!showAisles) return null;

  return (
    <Group>
      {Object.values(aisleZones).map(zone => (
        <AisleZoneShape
          key={zone.id}
          zone={zone}
          isSelected={selectedIds.includes(zone.id)}
          stageScale={stageScale}
            isDraggable={tool === 'select' && !zone.derivedFromRowIds}
          onSelect={additive => {
            if (tool === 'erase') { removeAisleZone(zone.id); return; }
            toggleSelected(zone.id, additive);
          }}
          onDragEnd={(x, y) => updateAisleZone(zone.id, { x, y })}
        />
      ))}
    </Group>
  );
};
